// Ручной ввод координат: формат записи и система координат.
//
// В карточке координаты всегда хранятся так же, как раньше: широта и долгота
// WGS-84 в десятичных градусах. Формат и система нужны только для ввода и
// показа — то, что человек переписывает с телефона или с лесоустроительных
// материалов, пересчитывается в WGS-84 при вводе и обратно при показе.
//
// Самая частая ошибка в поле — переписать «59°33′36″» как «59.3336»: это другая
// точка, на 25 км южнее. Поэтому и добавлен выбор формата.
//
// Библиотека пересчёта (proj4) подгружается отдельным файлом и только если
// выбрана не WGS-84 — у большинства пользователей она не скачается вовсе.
//
// СК-63 устроена иначе остальных систем: это не градусы, а метры (X, Y) в
// своей зоне своего района — её пересчёт ниже, отдельными функциями.

export const DEGREES = 'DEGREES';
export const DMS = 'DMS';

export const FORMAT_OPTIONS = [
  { value: DEGREES, label: 'Десятичные градусы' },
  { value: DMS, label: 'Градусы, минуты, секунды' },
];

export const WGS84 = 'WGS84';
export const SK63 = 'SK63';

export const DATUM_OPTIONS = [
  { value: WGS84, label: 'WGS-84' },
  { value: 'SK42', label: 'СК-42' },
  { value: SK63, label: 'СК-63' },
  { value: 'GSK2011', label: 'ГСК-2011' },
];

// Сдвиг СК-42 (эллипсоид Красовского) относительно WGS-84, ГОСТ Р 51794-2008.
// СК-63 построена на той же основе, поэтому и сдвиг у неё тот же.
const PULKOVO_TO_WGS84 = '+ellps=krass +towgs84=23.92,-141.27,-80.9,0,0.35,0.82,-0.12';

// Параметры пересчёта: эллипсоид и сдвиг относительно WGS-84.
// СК-42 — эллипсоид Красовского, расхождение с WGS-84 около 100–200 м.
// ГСК-2011 — современная система, от WGS-84 отличается на сантиметры.
const DATUM_DEFINITIONS = {
  SK42: `+proj=longlat ${PULKOVO_TO_WGS84} +no_defs`,
  GSK2011: '+proj=longlat +a=6378136.5 +rf=298.2564151 +towgs84=0,0,0,0,0,0,0 +no_defs',
};

// СК-63 — не широта и долгота, а плоские координаты в метрах: X — на север,
// Y — на восток, в проекции Гаусса — Крюгера. Страна поделена на районы
// (буквы), район — на зоны шириной 3° или 6°. Начало координат в каждом районе
// своё и смещено, поэтому без района и зоны X и Y не пересчитать: их пишут в
// легенде лесоустроительного планшета. Y начинается с номера зоны: 2 345 678 —
// это зона 2.
//
// Параметры зон — из открытого набора geo-ts/sk63 (Apache-2.0); зоны C1 и C2
// сверены с EPSG:3351 и 3352, зона C0 (Калининградская область) взята из
// EPSG:3350. Внутри района всё закономерно: осевой меридиан каждой следующей
// зоны — на ширину зоны восточнее, а смещение на восток — на 1 000 000 м больше.
//
// [район, ширина зоны, осевой меридиан первой зоны (градусы, минуты),
//  смещение первой зоны на восток, смещение на север, номер первой зоны, число зон]
const SK63_DISTRICT_TABLE = [
  ['C', 3, [21, 57], 250000, -11057.627, 0, 7],
  ['D', 3, [38, 33], 1250000, -14743.504, 1, 8],
  ['E', 3, [77, 47], 1300000, -14743.504, 1, 5],
  ['F', 3, [97, 2], 1250000, -11057.628, 1, 3],
  ['G', 3, [121, 43], 1300000, -16586.442, 1, 9],
  ['I', 3, [71, 44], 1250000, -12900.566, 1, 4],
  ['J', 6, [158, 28], 1400000, -16586.442, 1, 5],
  ['L', 6, [81, 31], 1500000, -16586.442, 1, 6],
  ['M', 3, [79, 28], 1300000, -12900.566, 1, 4],
  ['P', 3, [32, 29], 1250000, -12900.566, 1, 4],
  ['Q', 6, [32, 2], 1400000, -11057.628, 1, 5],
  ['R', 3, [43, 3], 1300000, -14743.501, 1, 3],
  ['S', 6, [108, 27], 1400000, -12900.566, 1, 13],
  ['T', 3, [37, 59], 1300000, -11057.628, 1, 4],
  ['V', 3, [49, 2], 1300000, -9414.7, 1, 6],
  ['W', 3, [60, 3], 1500000, -11057.628, 1, 8],
  ['W', 6, [60, 3], 1500000, -11057.628, 1, 4],
  ['X', 3, [23, 30], 1300000, -9214.69, 1, 6],
];

// Район W встречается с зонами обеих ширин — поэтому ширина входит в код района
export const SK63_DISTRICTS = SK63_DISTRICT_TABLE.map(
  ([letter, width, [degrees, minutes], easting, northing, firstZone, count]) => ({
    id: `${letter}${width}`,
    label: `Район ${letter}, зоны по ${width}°`,
    zones: Array.from({ length: count }, (_, index) => ({
      zone: firstZone + index,
      meridian: degrees + minutes / 60 + index * width,
      easting: easting + index * 1000000,
      northing,
    })),
  }),
);

export const DEFAULT_SK63_DISTRICT = SK63_DISTRICTS[0].id;

/** Зона района СК-63 по номеру. null — такой нет. */
export const sk63Zone = (districtId, zone) => {
  const district = SK63_DISTRICTS.find((item) => item.id === districtId);
  return (district && district.zones.find((item) => item.zone === Number(zone))) || null;
};

/** Зона района, в которую попадает долгота: та, чей осевой меридиан ближе всех. */
export const nearestSk63Zone = (districtId, lon) => {
  const district = SK63_DISTRICTS.find((item) => item.id === districtId);
  if (!district) return null;
  if (!Number.isFinite(lon)) return district.zones[0].zone;
  return district.zones.reduce((best, item) => (
    Math.abs(item.meridian - lon) < Math.abs(best.meridian - lon) ? item : best
  )).zone;
};

const sk63Projection = ({ meridian, easting, northing }) => (
  `+proj=tmerc +lat_0=0 +lon_0=${meridian} +k=1 +x_0=${easting} +y_0=${northing} ${PULKOVO_TO_WGS84} +units=m +no_defs`
);

// Насколько Y может отойти от осевого меридиана зоны. Зона в 6° на широте
// Кавказа — около 250 км в каждую сторону; с запасом берём 400 км. Ошибка в
// номере зоны даёт сдвиг на 1 000 000 м и отсюда сразу видна.
const SK63_MAX_OFFSET = 400000;

const formatMeters = (value) => Math.round(value).toLocaleString('ru-RU');

/**
 * Проверка X и Y СК-63 до пересчёта. Возвращает тексты ошибок по полям,
 * пустая строка — ошибки нет.
 */
export const sk63Errors = ({ x, y }, districtId, zone) => {
  const zoneDef = sk63Zone(districtId, zone);
  let xError = '';
  let yError = '';
  if (x === null) xError = 'Введите X';
  else if (x <= 0 || x >= 10000000) xError = 'X должен быть от 0 до 10 000 000 м';
  if (y === null) yError = 'Введите Y';
  else if (zoneDef && Math.abs(y - zoneDef.easting) > SK63_MAX_OFFSET) {
    yError = `Y не из зоны ${zoneDef.zone}: ожидается от ${formatMeters(zoneDef.easting - SK63_MAX_OFFSET)}`
      + ` до ${formatMeters(zoneDef.easting + SK63_MAX_OFFSET)} м — проверьте зону`;
  }
  return { x: xError, y: yError };
};

/** X и Y СК-63 — в широту и долготу WGS-84. */
export const sk63ToWgs84 = async ({ x, y }, districtId, zone) => {
  const zoneDef = sk63Zone(districtId, zone);
  if (!zoneDef) return null;
  const proj4 = await loadProjections();
  // В proj4 первой идёт координата на восток — это Y, второй на север — X
  const [lon, lat] = proj4(sk63Projection(zoneDef), WGS84, [y, x]);
  return { lat, lon };
};

/** Широта и долгота WGS-84 — в X и Y СК-63, чтобы показать их в полях. */
export const wgs84ToSk63 = async ({ lat, lon }, districtId, zone) => {
  const zoneDef = sk63Zone(districtId, zone);
  if (!zoneDef) return null;
  const proj4 = await loadProjections();
  const [y, x] = proj4(WGS84, sk63Projection(zoneDef), [lon, lat]);
  return { x, y };
};

let projections = null;

const loadProjections = async () => {
  if (!projections) {
    const proj4 = (await import('proj4')).default;
    Object.entries(DATUM_DEFINITIONS).forEach(([name, definition]) => proj4.defs(name, definition));
    projections = proj4;
  }
  return projections;
};

/** Число из поля ввода: запятая тоже годится как разделитель. null — не число. */
export const parseDegrees = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const num = Number(String(value).replace(',', '.'));
  return Number.isFinite(num) ? num : null;
};

const toNumber = parseDegrees;

/** Градусы, минуты и секунды — в десятичные градусы. Минуты и секунды могут быть дробными. */
export const dmsToDecimal = ({ degrees, minutes, seconds }) => {
  const d = toNumber(degrees);
  const m = minutes === '' || minutes === undefined ? 0 : toNumber(minutes);
  const s = seconds === '' || seconds === undefined ? 0 : toNumber(seconds);
  if (d === null || m === null || s === null) return null;
  if (m < 0 || m >= 60 || s < 0 || s >= 60) return null;
  // Знак берём у градусов: «-0» тоже означает южную широту или западную долготу
  const negative = d < 0 || String(degrees).trim().startsWith('-');
  const value = Math.abs(d) + m / 60 + s / 3600;
  return negative ? -value : value;
};

/** Десятичные градусы — в градусы, минуты и секунды (секунды с двумя знаками). */
export const decimalToDms = (value) => {
  const num = toNumber(value);
  if (num === null) return { degrees: '', minutes: '', seconds: '' };
  const negative = num < 0;
  let degrees = Math.floor(Math.abs(num));
  let minutes = Math.floor((Math.abs(num) - degrees) * 60);
  let seconds = Math.round(((Math.abs(num) - degrees) * 60 - minutes) * 60 * 100) / 100;
  // Округление секунд может дать ровно 60 — переносим в минуты, минуты в градусы
  if (seconds >= 60) {
    seconds = 0;
    minutes += 1;
  }
  if (minutes >= 60) {
    minutes = 0;
    degrees += 1;
  }
  return {
    degrees: String(negative ? -degrees : degrees),
    minutes: String(minutes),
    seconds: seconds.toFixed(2),
  };
};

/** Координаты выбранной системы — в WGS-84, как они хранятся в карточке. */
export const toWgs84 = async ({ lat, lon }, datum) => {
  if (datum === WGS84 || !DATUM_DEFINITIONS[datum]) return { lat, lon };
  const proj4 = await loadProjections();
  const [convertedLon, convertedLat] = proj4(datum, WGS84, [lon, lat]);
  return { lat: convertedLat, lon: convertedLon };
};

/** Координаты из карточки (WGS-84) — в выбранную систему, чтобы показать их в полях. */
export const fromWgs84 = async ({ lat, lon }, datum) => {
  if (datum === WGS84 || !DATUM_DEFINITIONS[datum]) return { lat, lon };
  const proj4 = await loadProjections();
  const [convertedLon, convertedLat] = proj4(WGS84, datum, [lon, lat]);
  return { lat: convertedLat, lon: convertedLon };
};
